import type { CSSProperties, PointerEventHandler, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface MapPanelChromeProps {
  /** Заголовок панели (строка или узел). */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Иконка/префикс слева от заголовка. */
  titleLeading?: ReactNode;
  /** Кнопки справа в шапке (закрытие, сохранить…). */
  headerActions?: ReactNode;
  /** Прокручиваемое тело. */
  children: ReactNode;
  /** Нижняя полоса primary-действий (не скроллится). */
  footer?: ReactNode;
  /** Доп. блок под шапкой (подсказки), без скролла. */
  belowHeader?: ReactNode;
  className?: string;
  style?: CSSProperties;
  bodyClassName?: string;
  /** Класс шапки (например cursor-grab для перетаскивания). */
  headerClassName?: string;
  onHeaderPointerDown?: PointerEventHandler<HTMLDivElement>;
  onHeaderPointerMove?: PointerEventHandler<HTMLDivElement>;
  onHeaderPointerUp?: PointerEventHandler<HTMLDivElement>;
  onHeaderPointerCancel?: PointerEventHandler<HTMLDivElement>;
  /** Без внешней рамки — когда оболочка уже задана снаружи (embedded). */
  bare?: boolean;
}

/**
 * Единый chrome панелей карты: шапка / опциональный belowHeader / scroll body / footer.
 */
export function MapPanelChrome({
  title,
  subtitle,
  titleLeading,
  headerActions,
  children,
  footer,
  belowHeader,
  className,
  style,
  bodyClassName,
  headerClassName,
  onHeaderPointerDown,
  onHeaderPointerMove,
  onHeaderPointerUp,
  onHeaderPointerCancel,
  bare = false,
}: MapPanelChromeProps) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden",
        !bare && "rounded-lg border border-border bg-card text-card-foreground shadow-lg",
        className,
      )}
      style={style}
    >
      {(title != null || headerActions != null) && (
        <div
          className={cn(
            "relative z-10 flex flex-shrink-0 items-start justify-between gap-2 border-b border-border p-3 [&_button]:cursor-pointer",
            headerClassName,
          )}
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerCancel}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {titleLeading}
            <div className="min-w-0">
              {title != null && (
                <div className="text-sm font-medium leading-tight text-foreground md:text-base">
                  {title}
                </div>
              )}
              {subtitle != null && subtitle !== "" && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground" title={typeof subtitle === "string" ? subtitle : undefined}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {headerActions != null && (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">{headerActions}</div>
          )}
        </div>
      )}
      {belowHeader}
      <div className={cn("min-h-0 flex-1 overflow-y-auto", bodyClassName)}>{children}</div>
      {footer != null && (
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border p-3">
          {footer}
        </div>
      )}
    </div>
  );
}
